// BatSeqMasterEngine.js
//
// FE Engine Pattern V2 (rk=beeca47db94541d594bc4aa830f62672) — initial fetch + SignalR
// OnBatSeqMastersUpdated (array of changed masters only) → addOrUpdate per rk.
import { events } from '@/services/wss/SystemEvents';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import WssEventHandlerBase from '@/services/engines/WssEventHandlerBase';
import batSeqService from '@/services/BatSeqService';
import responseHelper from '../../../services/ResponseHelper';
import batSeqMasterCache from './BatSeqMasterCache';

const _name = 'BatSeqMasterEngine';

class BatSeqMasterEngine extends WssEventHandlerBase {
    _name = _name;

    bootup() {
        super.bootup();
        this.clear();
        this._unsubMessage = this._wsNotifier.subscribe(events.OnBatSeqMastersUpdated, this.onMessage);
        this._unsubRemoved = this._wsNotifier.subscribe(events.OnBatSeqMastersDeleted, this.onRemoved);
    }

    clear() {
        super.clear();
        if (this._unsubMessage) this._unsubMessage();
        if (this._unsubRemoved) this._unsubRemoved();
    }

    /**
     * BE push — changed masters only.
     * @param {object[]} items
     */
    onMessage = (items) => {
        try {
            batSeqMasterCache.addOrUpdate(items ?? []);
            this._notify();
        } catch (error) {
            this.logError('onMessage', error.message);
        }
    };

    /**
     * BE push — removed masters, BE cascades their details.
     * @param {object[]} items
     */
    onRemoved = (items) => {
        try {
            items.forEach((x) => {
                batSeqMasterCache.delete(x.rk);
            });
            this._notify();
        } catch (error) {
            this.logError('onRemoved', error.message);
        }
    };

    /**
     * Initial fetch — all masters via REST.
     * @returns {Promise<object[]>}
     */
    fetch = async () => {
        try {
            const res = await batSeqService.getMasters();
            const items = res?.data?.items ?? [];
            batSeqMasterCache.setCache([...items]);
            this._notify();
            return items;
        } catch (error) {
            this.logError('fetch', error.message);
            return [];
        }
    };

    /**
     * Adds or updates a master, cache follows via SignalR push.
     * @param {object} data - BatSeqMaster DTO
     * @returns {Promise<boolean>} success
     */
    add = async (data) => {
        try {
            const resp = await batSeqService.postMasters([data]);
            return responseHelper.check(resp, '', _name) >= 0;
        } catch (error) {
            this.logError('add', error.message);
            return false;
        }
    };

    /**
     * Deletes a master, full DTO in body.
     * @param {object} master - BatSeqMaster DTO
     * @returns {Promise<boolean>} success
     */
    remove = async (master) => {
        try {
            const resp = await batSeqService.deleteMasters([master]);
            if (responseHelper.check(resp, '', _name) < 0) return false;
            batSeqMasterCache.delete(master.rk);
            this._notify();
            return true;
        } catch (error) {
            this.logError('remove', error.message);
            return false;
        }
    };

    /**
     * @param {string} did - battery rk
     * @returns {object[]}
     */
    getByDid = (did) => batSeqMasterCache.byDid(did);

    _notify = () => {
        ComponentNotifier.notify(_name, 'dataChanged', {});
    };
}

const batSeqMasterEngine = new BatSeqMasterEngine();
export default batSeqMasterEngine;
